import { Button, Dialog, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import { RowNode } from 'ag-grid-community';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import { useClassifierState } from '../../contexts/ClassifiersContext/ClassifiersContext';
import { ClassifiersTypes } from '../../contexts/ClassifiersContext/ClassifiersContext.types';
import { api } from '../../core/api';
import { SearchReportTypes } from '../../core/api/search';
import { CONSTANTS } from '../../core/config/constants';
import {
  RangeOflong,
  ReportFieldConfiguration,
  SearchQuerySaveRequest,
} from '../../generated/model/models';
import { useIsMount } from '../../hooks/useOnChange';
import {
  searchExportDefaultColumns,
  searchListColumnPresets,
} from '../../pages/FinancialData/Companies/SearchList/SearchListColumns';
import { useFilterState } from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext';
import { RequestParams } from '../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import YearRange from '../Inputs/YearRange';
import { allowedReportYearRange } from '../Inputs/YearRange/YearRange';
import TableGrid, { MainColumns } from '../TableGrid';
import TableGridColumnPresetsToolbar from '../TableGrid/Presets/TableGridColumnPresetsToolbar';
import DialogTitleBlock from './Dialog/DialogTitleBlock';
import { useRawReportModalStorage } from './useRawReportModalStorage';

interface OwnProps {
  open: boolean;
  handleClose: () => void;
  handleConfirmOk: (onClick: () => Promise<void>) => void;
  // searchVisibleCols: string[];
  queryRequest?: SearchQuerySaveRequest;
  companyId?: string;
  searchId?: string;
}

const RawReportColumnSelectionModal: React.FC<OwnProps> = ({
  open,
  handleClose,
  handleConfirmOk,
  // searchVisibleCols,
  queryRequest,
  companyId,
  searchId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const companyReportFields = useClassifierState(ClassifiersTypes.CompanyReportFields);
  const [state, setState] = useState<ReportFieldConfiguration>({
    reportFieldIds: [],
    finDataYearRange: allowedReportYearRange,
  });
  const [disabled, setDisabled] = useState(false);
  const isFirstMount = useIsMount();
  const { sortModel } = useFilterState();

  useEffect(() => {
    if (!isFirstMount && open) {
      setDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClick = async () => {
    setDisabled(true);
    if (companyId) {
      handleConfirmOk(async () => {
        ReactGA.event({ category: 'COMPANY', action: 'LIST_SELECTED_REPORT' });
        await api.search.getReportByCompanyId(SearchReportTypes.RAW, companyId, searchId, state);
      });
    } else if (queryRequest) {
      const params: RequestParams = {};

      if (sortModel?.length) {
        params.sort = sortModel.map((sortProperty) => `${sortProperty.colId},${sortProperty.sort}`);
      }

      handleConfirmOk(async () => {
        ReactGA.event({ category: 'SEARCH', action: 'LIST_SELECTED_REPORT' });
        await api.search.getReport(
          SearchReportTypes.RAW,
          { ...queryRequest, config: state },
          params,
        );
      });
    }
  };

  const { selectedRows, selectedRange, onRowsChanged, onRangeChanged } = useRawReportModalStorage({
    defaultSelectedRows: searchExportDefaultColumns(),
    defaultSelectedRange: allowedReportYearRange,
  });

  const onSelectionChanged = () => (ids: string[]) => {
    setState({ ...state, reportFieldIds: ids });
    onRowsChanged(ids);
  };

  const onYearsChanged = (range: RangeOflong) => {
    setState({ ...state, finDataYearRange: range });
    onRangeChanged(range);
  };

  const rowSelectionIdGetter = (node: RowNode) => {
    return node.data?.id ?? '';
  };

  const columnsStatus: MainColumns[] = [
    {
      field: 'name',
      minWidth: 150,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterParams: { suppressAndOrCondition: true },
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
  ];

  return (
    <Dialog
      maxWidth="lg"
      aria-labelledby="confirmation-dialog-title"
      open={open}
      onClose={handleClose}
      className={classes.dialogBox}
    >
      <DialogTitleBlock
        icon={<TuneIcon />}
        title={t('companies:reportDialog:header')}
        handleClose={handleClose}
        disabled={disabled}
      />
      <DialogContent>
        <Typography>{t('companies:reportDialog:rangeSelection')}</Typography>
        <YearRange
          root={true}
          field={'rawListYearsRange'}
          defaultValue={selectedRange}
          onChange={(range) => onYearsChanged(range)}
        />

        <TableGrid
          columns={columnsStatus}
          tableToolbar={
            <TableGridColumnPresetsToolbar
              systemDefaultFields={searchExportDefaultColumns()}
              systemDefaultPresets={searchListColumnPresets()}
              selectedColumns={state.reportFieldIds || []}
            />
          }
          rowSelection={true}
          isShowSideBar={false}
          rowSelectionIdGetter={rowSelectionIdGetter}
          translationKey={'companies:reportDialog:columns'}
          initialSelectedRows={selectedRows}
          onSelectionChanged={onSelectionChanged()}
          originalProps={{
            headerHeight: CONSTANTS.TABLE_HEADER - 1,
            rowData: companyReportFields,
            rowModelType: 'clientSide',
            defaultColDef: {
              resizable: false,
              flex: 1,
              menuTabs: [],
              autoHeight: false,
              wrapText: false,
              sortable: false,
            },
            rowSelection: 'multiple',
            domLayout: 'autoHeight',
            rowMultiSelectWithClick: true,
            suppressRowClickSelection: true,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button autoFocus onClick={handleClose} color="primary">
          {t('app:cancel')}
        </Button>
        <Button onClick={handleClick} color="primary" variant="contained" disabled={disabled}>
          {t('app:ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  input: {
    padding: theme.spacing(0),
  },
  dialogBox: {
    '& .MuiPaper-root': {
      width: '550px',
      height: 'calc(100vh - 110px)',
    },
    '& .MuiDialogContent-root .MuiPaper-root': {
      borderRadius: 0,
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    color: theme.palette.grey[500],
  },
}));

export default RawReportColumnSelectionModal;
